/**
 * Chat Sound Presets
 * Manages chat notification sounds from assets/ChatSounds directory
 */

export const CHAT_SOUND_PRESETS = [
  {
    id: 'auramessage',
    name: 'Aura Message',
    file: 'ChatSounds/auramessage.wav',
    isPlus: false,
  },
  {
    id: 'notespulse',
    name: 'Notes+ Pulse',
    file: 'ChatSounds/notespulse.wav',
    isPlus: false,
  },
  {
    id: 'novaping',
    name: 'Nova Ping',
    file: 'ChatSounds/novaping.wav',
    isPlus: false,
  },
  {
    id: 'rumi',
    name: 'Rumi',
    file: 'ChatSounds/rumi.wav',
    isPlus: false,
  },
  {
    id: 'softpulse',
    name: 'Soft Pulse',
    file: 'ChatSounds/softpulse.wav',
    isPlus: false,
  },
];

// Plus Ses Paketi - Sadece Plus kullanıcıları için
export const PLUS_CHAT_SOUND_PRESETS = [
  {
    id: 'plus-elite',
    name: 'Plus Elite',
    file: 'ChatSounds/plus-elite.wav',
    isPlus: true,
  },
  {
    id: 'plus-crystal',
    name: 'Plus Crystal',
    file: 'ChatSounds/plus-crystal.wav',
    isPlus: true,
  },
  {
    id: 'plus-diamond',
    name: 'Plus Diamond',
    file: 'ChatSounds/plus-diamond.wav',
    isPlus: true,
  },
  {
    id: 'plus-premium',
    name: 'Plus Premium',
    file: 'ChatSounds/plus-premium.wav',
    isPlus: true,
  },
];

export const DEFAULT_CHAT_SOUND_ID = CHAT_SOUND_PRESETS[0]?.id || 'auramessage';

// Default chat message sound (used when user is in chat tab)
export const CHAT_INTERNAL_SOUND = {
  id: 'newmessagesound',
  name: 'New Message Sound',
  file: 'ChatSounds/newmessagesound.wav',
};

const resolveAssetUrl = async (fileReference) => {
  if (!fileReference) {
    return null;
  }

  const toSegments = (reference) => {
    if (Array.isArray(reference)) {
      return reference;
    }
    if (typeof reference === 'string') {
      return reference.split('/').filter(Boolean);
    }
    return [];
  };

  const segments = toSegments(fileReference);

  if (typeof window !== 'undefined' && window.api?.getAssetUrl) {
    try {
      const url = await window.api.getAssetUrl(segments);
      if (url) {
        return url;
      }
    } catch (error) {
      console.warn("Chat ses dosyası URL'si alınamadı:", error);
    }
  }

  const joined = segments.join('/');
  if (!joined) {
    return null;
  }

  if (/^https?:\/\//i.test(joined) || joined.startsWith('file://')) {
    return joined;
  }

  const base = (typeof process !== 'undefined' && process?.env?.PUBLIC_URL) || '';
  const normalizedBase = base ? base.replace(/\/+$/, '') : '';
  const encodedPath = joined
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');

  if (normalizedBase) {
    return `${normalizedBase}/assets/${encodedPath}`;
  }
  return `assets/${encodedPath}`;
};

// Current playing audio instance (to prevent overlapping sounds)
let currentAudioInstance = null;

/**
 * Play a chat sound file
 * @param {string} soundId - Sound ID from CHAT_SOUND_PRESETS or 'newmessagesound' for internal sound
 * @returns {Promise<void>}
 */
export const playChatSound = async (soundId) => {
  if (typeof window === 'undefined' || typeof Audio === 'undefined') {
    return Promise.resolve();
  }

  // Stop any currently playing sound
  if (currentAudioInstance) {
    try {
      currentAudioInstance.pause();
      currentAudioInstance.currentTime = 0;
      currentAudioInstance = null;
    } catch (error) {
      // Ignore errors when stopping
    }
  }

  // Determine which sound to play
  let soundFile = null;
  if (soundId === 'newmessagesound' || soundId === CHAT_INTERNAL_SOUND.id) {
    soundFile = CHAT_INTERNAL_SOUND.file;
  } else {
    // Check regular presets first
    let preset = CHAT_SOUND_PRESETS.find((item) => item.id === soundId);
    // If not found, check Plus presets
    if (!preset) {
      preset = PLUS_CHAT_SOUND_PRESETS.find((item) => item.id === soundId);
    }
    if (!preset) {
      console.warn(`Chat ses bulunamadı: ${soundId}, varsayılan ses kullanılıyor`);
      soundFile = CHAT_SOUND_PRESETS[0]?.file || CHAT_INTERNAL_SOUND.file;
    } else {
      soundFile = preset.file;
    }
  }

  return new Promise(async (resolve, reject) => {
    let audio = null;
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (audio) {
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
        if (currentAudioInstance === audio) {
          currentAudioInstance = null;
        }
      }
    };

    const handleEnded = () => {
      cleanup();
      resolve();
    };

    const handleError = (event) => {
      cleanup();
      reject(event?.error || new Error('Chat ses dosyası oynatılamadı'));
    };

    try {
      const src = await resolveAssetUrl(soundFile);
      if (!src) {
        throw new Error('Chat ses dosyasının yolu bulunamadı.');
      }

      audio = new Audio(src);
      audio.volume = 0.7; // Default volume for chat sounds

      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);

      currentAudioInstance = audio;

      const playResult = audio.play();
      if (playResult && typeof playResult.then === 'function') {
        playResult.catch((error) => {
          cleanup();
          
          // Handle Autoplay Policy Error (NotAllowedError)
          if (error.name === 'NotAllowedError' || error.name === 'NotAllowed') {
            console.warn('🔇 [ChatSound] Autoplay blocked by browser policy. Ses çalınamadı:', error.message);
            
            // Optional: Show notification to user that sound was blocked
            if (typeof window !== 'undefined') {
              // Silently fail - don't show error to user for autoplay blocks
              // Audio autoplay is often blocked by browsers, this is expected behavior
              resolve(); // Resolve instead of reject to prevent error bubbling
              return;
            }
          }
          
          // For other errors, reject
          reject(error);
        });
      }

      // Max duration: 5 seconds (safety timeout)
      timeoutId = window.setTimeout(() => {
        cleanup();
        resolve();
      }, 5000);
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
};
